import React, { useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { Button } from "../components/ui/button";
import { Link, useNavigate } from "react-router-dom";
import HotelOutlinedIcon from '@mui/icons-material/HotelOutlined';
import SellOutlinedIcon from '@mui/icons-material/SellOutlined';
import CardGiftcardOutlinedIcon from '@mui/icons-material/CardGiftcardOutlined';
import WifiOutlinedIcon from '@mui/icons-material/WifiOutlined';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import WorkOutlineOutlinedIcon from '@mui/icons-material/WorkOutlineOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import FacebookOutlinedIcon from '@mui/icons-material/FacebookOutlined';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import CheckIcon from '@mui/icons-material/Check';
import * as Yup from "yup";
import axios from "axios";
import { setAuthData, redirectToExternalApp } from '../utils/authHelper';
import { getExternalWebsiteUrl, EXTERNAL_CONFIG } from '../config/externalConfig';

const SignIn = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [formData, setFormData] = useState({
    country: "IN",
    countryCode: "+91",
    mobile: "",
    password: "",
    rememberMe: true
  });
  const [focusedField, setFocusedField] = useState(null);
  const [errors, setErrors] = useState({});
  
  const [expandedSections, setExpandedSections] = useState({
    topDestinations: false,
    forGuests: false,
    ourCompany: false
  });

  // Validation schema
  const validationSchema = Yup.object({
    mobile: Yup.string()
      .matches(/^\d{10}$/, "Mobile number must be exactly 10 digits")
      .required("Mobile number is required"),
    password: Yup.string()
      .min(1, "Password is required")
      .required("Password is required"),
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    let finalValue = value;
    
    // For mobile field, only allow numbers
    if (name === 'mobile') {
      finalValue = value.replace(/[^0-9]/g, '');
    }
    
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : finalValue
    });

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  const handleCountryChange = (e) => {
    const { value } = e.target;
    const countryCode = e.target.options[e.target.selectedIndex].getAttribute('data-code');
    
    setFormData({
      ...formData,
      country: value,
      countryCode: countryCode
    });
  };

  const handleFocus = (fieldName) => {
    setFocusedField(fieldName);
  };

  const handleBlur = () => {
    setFocusedField(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrors({});

    try {
      // Validate form data
      await validationSchema.validate(formData, { abortEarly: false });
      
      // Make API call
      const apiUrl = process.env.REACT_APP_API_BASE_URL;
      if (!apiUrl) {
        throw new Error('API base URL is not configured. Please check your environment variables.');
      }
      
      console.log('Using API URL:', apiUrl);
      
      const response = await axios.post(
        `${apiUrl}/api/auth/login`,
        {
          phoneNo: formData.mobile,
          password: formData.password,
          countryCode: formData.countryCode,
        }
      );

      if (response.status === 200) {
        setSuccessMessage(response.data.message || "Login successful!");
        setShowSuccessAlert(true);
        
        // Store authentication data for cross-domain access
        if (response.data.token) {
          setAuthData(response.data.token, response.data.user);
        }
        
        // Auto-hide alert after configured delay and redirect to external website
        setTimeout(() => {
          setShowSuccessAlert(false);
          // Redirect to your other website/project
          const externalUrl = getExternalWebsiteUrl();
          console.log('Redirecting to external URL:', externalUrl);
          redirectToExternalApp(externalUrl, response.data.token, response.data.user);
        }, EXTERNAL_CONFIG.REDIRECT.SUCCESS_DELAY);
      }
    } catch (error) {
      if (error.name === 'ValidationError') {
        // Yup validation errors
        const validationErrors = {};
        error.inner.forEach((err) => {
          validationErrors[err.path] = err.message;
        });
        setErrors(validationErrors);
      } else if (error.response) {
        // API errors
        const apiErrors = error.response.data.errors;
        const errorMessage = error.response.data.message;
        
        if (apiErrors) {
          setErrors(apiErrors);
        }
        
        // Show error alert with the main message
        if (errorMessage) {
          setErrorMessage(errorMessage);
          setShowErrorAlert(true);
          setTimeout(() => setShowErrorAlert(false), 5000);
        } else {
          setErrorMessage("Login failed. Please check your credentials and try again.");
          setShowErrorAlert(true);
          setTimeout(() => setShowErrorAlert(false), 5000);
        }
      } else {
        setErrorMessage("Network error. Please check your connection and try again.");
        setShowErrorAlert(true);
        setTimeout(() => setShowErrorAlert(false), 5000);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  return (
    <div className="min-h-screen bg-white">
      
      {/* Success Alert */}
      {showSuccessAlert && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 w-full max-w-md">
          <Alert 
            severity="success" 
            icon={<CheckIcon fontSize="inherit" />}
            onClose={() => setShowSuccessAlert(false)}
            sx={{ 
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
              borderRadius: '8px',
              fontFamily: 'Swiss-721, Arial, sans-serif'
            }}
          >
            <AlertTitle sx={{ fontFamily: 'Swiss-721, Arial, sans-serif', fontWeight: 600 }}>
              Success
            </AlertTitle>
            {successMessage}
          </Alert>
        </div>
      )}

      {/* Error Alert */}
      {showErrorAlert && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 w-full max-w-md">
          <Alert 
            severity="error" 
            onClose={() => setShowErrorAlert(false)}
            sx={{ 
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
              borderRadius: '8px',
              fontFamily: 'Swiss-721, Arial, sans-serif'
            }}
          >
            <AlertTitle sx={{ fontFamily: 'Swiss-721, Arial, sans-serif', fontWeight: 600 }}>
              Error
            </AlertTitle>
            {errorMessage}
          </Alert>
        </div>
      )}
      {/* Marriott Bonvoy Header */}
      <header className="bg-white transition-all duration-300">
        <div className="max-w-7xl mx-auto px-4">
          {/* First Row - Logo and Right Aligned Utility Links */}
          <div className="flex items-center justify-between h-28 transition-all duration-300">
            {/* Ritz-Carlton Logo */}
            <Link to="/" className="flex items-center ml-0">
              <img 
                src="/images/the-ritz-carlton-logo.png"
                alt="The Ritz-Carlton"
                style={{
                  width: '133px',
                  height: '112px',
                  objectFit: 'contain'
                }}
              />
            </Link>

            {/* Utility Links - Right Aligned */}
            <div className="flex items-center space-x-6" style={{ marginTop: '-40px' }}>
              <Link to="/help" className="flex items-center space-x-1 text-black hover:text-black font-normal">
                <HelpOutlineOutlinedIcon style={{ width: '21px', height: '18px' }} />
                <span className="text-sm">Help</span>
              </Link>
              
              <div className="flex items-center space-x-1 text-black hover:text-black font-normal">
                <PublicOutlinedIcon style={{ width: '21px', height: '18px' }} />
                <span className="text-sm">English</span>
              </div>
              
              <Link to="/trips" className="flex items-center space-x-1 text-black hover:text-black font-normal">
                <WorkOutlineOutlinedIcon style={{ width: '21px', height: '18px' }} />
                <span className="text-sm">My Trips</span>
              </Link>
              
              <Link to="/signin" className="flex items-center space-x-1 text-black hover:text-black font-normal">
                <PersonOutlineOutlinedIcon style={{ width: '21px', height: '18px' }} />
                <span className="text-sm">Sign In Or Join</span>
              </Link>
            </div>
          </div>

          {/* Divider Line with Shadow */}
          <div className="h-px bg-gray-300 shadow-sm w-full"></div>

          {/* Second Row - Navigation */}
          <div className="flex items-center justify-center h-0.5 -mt-5">
            {/* Navigation Menu */}
            <nav className="flex items-center space-x-8">
              <Link to="/find-reserve" className="text-[#1c1c1c] hover:text-[#1c1c1c] transition-colors duration-200 antialiased" style={{ fontFamily: 'Swiss-721, Arial, sans-serif', fontSize: '14px', fontWeight: 700 }}>
                Find & Reserve
              </Link>
              <Link to="/offers" className="text-[#1c1c1c] hover:text-[#1c1c1c] transition-colors duration-200 antialiased" style={{ fontFamily: 'Swiss-721, Arial, sans-serif', fontSize: '14px', fontWeight: 700 }}>
                Special Offers
              </Link>
              <Link to="/vacations" className="text-[#1c1c1c] hover:text-[#1c1c1c] transition-colors duration-200 antialiased" style={{ fontFamily: 'Swiss-721, Arial, sans-serif', fontSize: '14px', fontWeight: 700 }}>
                Vacations
              </Link>
              <Link to="/brands" className="text-[#1c1c1c] hover:text-[#1c1c1c] transition-colors duration-200 antialiased" style={{ fontFamily: 'Swiss-721, Arial, sans-serif', fontSize: '14px', fontWeight: 700 }}>
                Our Brands
              </Link>
              <Link to="/credit-cards" className="text-[#1c1c1c] hover:text-[#1c1c1c] transition-colors duration-200 antialiased" style={{ fontFamily: 'Swiss-721, Arial, sans-serif', fontSize: '14px', fontWeight: 700 }}>
                Our Credit Cards
              </Link>
              <Link to="/about" className="text-[#1c1c1c] hover:text-[#1c1c1c] transition-colors duration-200 antialiased" style={{ fontFamily: 'Swiss-721, Arial, sans-serif', fontSize: '14px', fontWeight: 700 }}>
                About Marriott Bonvoy
              </Link>
              <Link to="/meetings" className="text-[#1c1c1c] hover:text-[#1c1c1c] transition-colors duration-200 antialiased" style={{ fontFamily: 'Swiss-721, Arial, sans-serif', fontSize: '14px', fontWeight: 700 }}>
                Meetings & Events
              </Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 relative">
          
          {/* Left Section - Sign In */}
          <div className="space-y-8">
            <div>
              <h1 
                className="text-gray-900 mb-2"
                style={{
                  fontFamily: 'Swiss-721, Arial, sans-serif',
                  fontSize: '30px',
                  fontWeight: 700,
                  height: '45px',
                  paddingLeft: '8px',
                  paddingRight: '8px',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                Welcome to Marriott Bonvoy
              </h1>
              <h2 className="text-xl font-semibold text-gray-900">
                Sign In
              </h2>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Country Field */}
              <div className="relative" style={{ width: '458px' }}>
                <label 
                  htmlFor="country" 
                  className="block mb-2"
                  style={{ 
                    fontFamily: 'Swiss-721, Arial, sans-serif', 
                    fontWeight: 500, 
                    color: 'rgb(112, 112, 112)',
                    fontSize: '14px'
                  }}
                >
                  Country
                </label>
                <select
                  id="country"
                  name="country"
                  value={formData.country}
                  onChange={handleCountryChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  style={{ fontFamily: 'Swiss-721, Arial, sans-serif' }}
                >
                  <option value="">Select a country</option>
                  <option value="AF" data-flag="🇦🇫" data-code="+93">🇦🇫 +93</option>
                  <option value="AL" data-flag="🇦🇱" data-code="+355">🇦🇱 +355</option>
                  <option value="DZ" data-flag="🇩🇿" data-code="+213">🇩🇿 +213</option>
                  <option value="AS" data-flag="🇦🇸" data-code="+1">🇦🇸 +1</option>
                  <option value="AD" data-flag="🇦🇩" data-code="+376">🇦🇩 +376</option>
                  <option value="AO" data-flag="🇦🇴" data-code="+244">🇦🇴 +244</option>
                  <option value="AI" data-flag="🇦🇮" data-code="+1">🇦🇮 +1</option>
                  <option value="AQ" data-flag="🇦🇶" data-code="+672">🇦🇶 +672</option>
                  <option value="AG" data-flag="🇦🇬" data-code="+1">🇦🇬 +1</option>
                  <option value="AR" data-flag="🇦🇷" data-code="+54">🇦🇷 +54</option>
                  <option value="AM" data-flag="🇦🇲" data-code="+374">🇦🇲 +374</option>
                  <option value="AW" data-flag="🇦🇼" data-code="+297">🇦🇼 +297</option>
                  <option value="AU" data-flag="🇦🇺" data-code="+61">🇦🇺 +61</option>
                  <option value="AT" data-flag="🇦🇹" data-code="+43">🇦🇹 +43</option>
                  <option value="AZ" data-flag="🇦🇿" data-code="+994">🇦🇿 +994</option>
                  <option value="BS" data-flag="🇧🇸" data-code="+1">🇧🇸 +1</option>
                  <option value="BH" data-flag="🇧🇭" data-code="+973">🇧🇭 +973</option>
                  <option value="BD" data-flag="🇧🇩" data-code="+880">🇧🇩 +880</option>
                  <option value="BB" data-flag="🇧🇧" data-code="+1">🇧🇧 +1</option>
                  <option value="BY" data-flag="🇧🇾" data-code="+375">🇧🇾 +375</option>
                  <option value="BE" data-flag="🇧🇪" data-code="+32">🇧🇪 +32</option>
                  <option value="BZ" data-flag="🇧🇿" data-code="+501">🇧🇿 +501</option>
                  <option value="BJ" data-flag="🇧🇯" data-code="+229">🇧🇯 +229</option>
                  <option value="BM" data-flag="🇧🇲" data-code="+1">🇧🇲 +1</option>
                  <option value="BT" data-flag="🇧🇹" data-code="+975">🇧🇹 +975</option>
                  <option value="BO" data-flag="🇧🇴" data-code="+591">🇧🇴 +591</option>
                  <option value="BA" data-flag="🇧🇦" data-code="+387">🇧🇦 +387</option>
                  <option value="BW" data-flag="🇧🇼" data-code="+267">🇧🇼 +267</option>
                  <option value="BV" data-flag="🇧🇻" data-code="+47">🇧🇻 +47</option>
                  <option value="BR" data-flag="🇧🇷" data-code="+55">🇧🇷 +55</option>
                  <option value="IO" data-flag="🇮🇴" data-code="+246">🇮🇴 +246</option>
                  <option value="BN" data-flag="🇧🇳" data-code="+673">🇧🇳 +673</option>
                  <option value="BG" data-flag="🇧🇬" data-code="+359">🇧🇬 +359</option>
                  <option value="BF" data-flag="🇧🇫" data-code="+226">🇧🇫 +226</option>
                  <option value="BI" data-flag="🇧🇮" data-code="+257">🇧🇮 +257</option>
                  <option value="KH" data-flag="🇰🇭" data-code="+855">🇰🇭 +855</option>
                  <option value="CM" data-flag="🇨🇲" data-code="+237">🇨🇲 +237</option>
                  <option value="CA" data-flag="🇨🇦" data-code="+1">🇨🇦 +1</option>
                  <option value="CV" data-flag="🇨🇻" data-code="+238">🇨🇻 +238</option>
                  <option value="KY" data-flag="🇰🇾" data-code="+1">🇰🇾 +1</option>
                  <option value="CF" data-flag="🇨🇫" data-code="+236">🇨🇫 +236</option>
                  <option value="TD" data-flag="🇹🇩" data-code="+235">🇹🇩 +235</option>
                  <option value="CL" data-flag="🇨🇱" data-code="+56">🇨🇱 +56</option>
                  <option value="CN" data-flag="🇨🇳" data-code="+86">🇨🇳 +86</option>
                  <option value="CX" data-flag="🇨🇽" data-code="+61">🇨🇽 +61</option>
                  <option value="CC" data-flag="🇨🇨" data-code="+61">🇨🇨 +61</option>
                  <option value="CO" data-flag="🇨🇴" data-code="+57">🇨🇴 +57</option>
                  <option value="KM" data-flag="🇰🇲" data-code="+855">🇰🇲 +855</option>
                  <option value="CG" data-flag="🇨🇬" data-code="+242">🇨🇬 +242</option>
                  <option value="CD" data-flag="🇨🇩" data-code="+243">🇨🇩 +243</option>
                  <option value="CK" data-flag="🇨🇰" data-code="+682">🇨🇰 +682</option>
                  <option value="CR" data-flag="🇨🇷" data-code="+506">🇨🇷 +506</option>
                  <option value="CI" data-flag="🇨🇮" data-code="+225">🇨🇮 +225</option>
                  <option value="HR" data-flag="🇭🇷" data-code="+385">🇭🇷 +385</option>
                  <option value="CU" data-flag="🇨🇺" data-code="+53">🇨🇺 +53</option>
                  <option value="CY" data-flag="🇨🇾" data-code="+357">🇨🇾 +357</option>
                  <option value="CZ" data-flag="🇨🇿" data-code="+420">🇨🇿 +420</option>
                  <option value="DK" data-flag="🇩🇰" data-code="+45">🇩🇰 +45</option>
                  <option value="DJ" data-flag="🇩🇯" data-code="+253">🇩🇯 +253</option>
                  <option value="DM" data-flag="🇩🇲" data-code="+1">🇩🇲 +1</option>
                  <option value="DO" data-flag="🇩🇴" data-code="+1">🇩🇴 +1</option>
                  <option value="EC" data-flag="🇪🇨" data-code="+593">🇪🇨 +593</option>
                  <option value="EG" data-flag="🇪🇬" data-code="+20">🇪🇬 +20</option>
                  <option value="SV" data-flag="🇸🇻" data-code="+503">🇸🇻 +503</option>
                  <option value="GQ" data-flag="🇬🇶" data-code="+240">🇬🇶 +240</option>
                  <option value="ER" data-flag="🇪🇷" data-code="+291">🇪🇷 +291</option>
                  <option value="EE" data-flag="🇪🇪" data-code="+372">🇪🇪 +372</option>
                  <option value="ET" data-flag="🇪🇹" data-code="+251">🇪🇹 +251</option>
                  <option value="FK" data-flag="🇫🇰" data-code="+500">🇫🇰 +500</option>
                  <option value="FO" data-flag="🇫🇴" data-code="+298">🇫🇴 +298</option>
                  <option value="FJ" data-flag="🇫🇯" data-code="+679">🇫🇯 +679</option>
                  <option value="FI" data-flag="🇫🇮" data-code="+358">🇫🇮 +358</option>
                  <option value="FR" data-flag="🇫🇷" data-code="+33">🇫🇷 +33</option>
                  <option value="GF" data-flag="🇬🇫" data-code="+594">🇬🇫 +594</option>
                  <option value="PF" data-flag="🇵🇫" data-code="+689">🇵🇫 +689</option>
                  <option value="TF" data-flag="🇹🇫" data-code="+262">🇹🇫 +262</option>
                  <option value="GA" data-flag="🇬🇦" data-code="+240">🇬🇦 +240</option>
                  <option value="GM" data-flag="🇬🇲" data-code="+220">🇬🇲 +220</option>
                  <option value="GE" data-flag="🇬🇪" data-code="+995">🇬🇪 +995</option>
                  <option value="DE" data-flag="🇩🇪" data-code="+49">🇩🇪 +49</option>
                  <option value="GH" data-flag="🇬🇭" data-code="+233">🇬🇭 +233</option>
                  <option value="GI" data-flag="🇬🇮" data-code="+350">🇬🇮 +350</option>
                  <option value="GR" data-flag="🇬🇷" data-code="+30">🇬🇷 +30</option>
                  <option value="GL" data-flag="🇬🇱" data-code="+299">🇬🇱 +299</option>
                  <option value="GD" data-flag="🇬🇩" data-code="+1">🇬🇩 +1</option>
                  <option value="GP" data-flag="🇬🇵" data-code="+590">🇬🇵 +590</option>
                  <option value="GU" data-flag="🇬🇺" data-code="+1">🇬🇺 +1</option>
                  <option value="GT" data-flag="🇬🇹" data-code="+502">🇬🇹 +502</option>
                  <option value="GG" data-flag="🇬🇬" data-code="+44">🇬🇬 +44</option>
                  <option value="GN" data-flag="🇬🇳" data-code="+224">🇬🇳 +224</option>
                  <option value="GW" data-flag="🇬🇼" data-code="+245">🇬🇼 +245</option>
                  <option value="GY" data-flag="🇬🇾" data-code="+592">🇬🇾 +592</option>
                  <option value="HT" data-flag="🇭🇹" data-code="+509">🇭🇹 +509</option>
                  <option value="HM" data-flag="🇭🇲" data-code="+672">🇭🇲 +672</option>
                  <option value="VA" data-flag="🇻🇦" data-code="+379">🇻🇦 +379</option>
                  <option value="HN" data-flag="🇭🇳" data-code="+504">🇭🇳 +504</option>
                  <option value="HK" data-flag="🇭🇰" data-code="+852">🇭🇰 +852</option>
                  <option value="HU" data-flag="🇭🇺" data-code="+389">🇭🇺 +389</option>
                  <option value="IS" data-flag="🇮🇸" data-code="+354">🇮🇸 +354</option>
                  <option value="IN" data-flag="🇮🇳" data-code="+91">🇮🇳 +91</option>
                  <option value="ID" data-flag="🇮🇩" data-code="+62">🇮🇩 +62</option>
                  <option value="IR" data-flag="🇮🇷" data-code="+98">🇮🇷 +98</option>
                  <option value="IQ" data-flag="🇮🇶" data-code="+964">🇮🇶 +964</option>
                  <option value="IE" data-flag="🇮🇪" data-code="+353">🇮🇪 +353</option>
                  <option value="IM" data-flag="🇮🇲" data-code="+44">🇮🇲 +44</option>
                  <option value="IL" data-flag="🇮🇱" data-code="+972">🇮🇱 +972</option>
                  <option value="IT" data-flag="🇮🇹" data-code="+39">🇮🇹 +39</option>
                  <option value="JM" data-flag="🇯🇲" data-code="+1">🇯🇲 +1</option>
                  <option value="JP" data-flag="🇯🇵" data-code="+126">🇯🇵 +126</option>
                  <option value="JE" data-flag="🇯🇪" data-code="+44">🇯🇪 +44</option>
                  <option value="JO" data-flag="🇯🇴" data-code="+962">🇯🇴 +962</option>
                  <option value="KZ" data-flag="🇰🇿" data-code="+7">🇰🇿 +7</option>
                  <option value="KE" data-flag="🇰🇪" data-code="+254">🇰🇪 +254</option>
                  <option value="KI" data-flag="🇰🇮" data-code="+686">🇰🇮 +686</option>
                  <option value="KP" data-flag="🇰🇵" data-code="+850">🇰🇵 +850</option>
                  <option value="KR" data-flag="🇰🇷" data-code="+82">🇰🇷 +82</option>
                  <option value="KW" data-flag="🇰🇼" data-code="+965">🇰🇼 +965</option>
                  <option value="KG" data-flag="🇰🇬" data-code="+996">🇰🇬 +996</option>
                  <option value="LA" data-flag="🇱🇦" data-code="+856">🇱🇦 +856</option>
                  <option value="LV" data-flag="🇱🇻" data-code="+371">🇱🇻 +371</option>
                  <option value="LB" data-flag="🇱🇧" data-code="+961">🇱🇧 +961</option>
                  <option value="LS" data-flag="🇱🇸" data-code="+266">🇱🇸 +266</option>
                  <option value="LR" data-flag="🇱🇷" data-code="+231">🇱🇷 +231</option>
                  <option value="LY" data-flag="🇱🇾" data-code="+218">🇱🇾 +218</option>
                  <option value="LI" data-flag="🇱🇮" data-code="+423">🇱🇮 +423</option>
                  <option value="LT" data-flag="🇱🇹" data-code="+370">🇱🇹 +370</option>
                  <option value="LU" data-flag="🇱🇺" data-code="+352">🇱🇺 +352</option>
                  <option value="MO" data-flag="🇲🇴" data-code="+853">🇲🇴 +853</option>
                  <option value="MK" data-flag="🇲🇰" data-code="+389">🇲🇰 +389</option>
                  <option value="MG" data-flag="🇲🇬" data-code="+261">🇲🇬 +261</option>
                  <option value="MW" data-flag="🇲🇼" data-code="+265">🇲🇼 +265</option>
                  <option value="MY" data-flag="🇲🇾" data-code="+60">🇲🇾 +60</option>
                  <option value="MV" data-flag="🇲🇻" data-code="+960">🇲🇻 +960</option>
                  <option value="ML" data-flag="🇲🇱" data-code="+223">🇲🇱 +223</option>
                  <option value="MT" data-flag="🇲🇹" data-code="+356">🇲🇹 +356</option>
                  <option value="MH" data-flag="🇲🇭" data-code="+692">🇲🇭 +692</option>
                  <option value="MQ" data-flag="🇲🇶" data-code="+596">🇲🇶 +596</option>
                  <option value="MR" data-flag="🇲🇷" data-code="+222">🇲🇷 +222</option>
                  <option value="MU" data-flag="🇲🇺" data-code="+230">🇲🇺 +230</option>
                  <option value="YT" data-flag="🇾🇹" data-code="+262">🇾🇹 +262</option>
                  <option value="MX" data-flag="🇲🇽" data-code="+52">🇲🇽 +52</option>
                  <option value="FM" data-flag="🇫🇲" data-code="+691">🇫🇲 +691</option>
                  <option value="MD" data-flag="🇲🇩" data-code="+373">🇲🇩 +373</option>
                  <option value="MC" data-flag="🇲🇨" data-code="+377">🇲🇨 +377</option>
                  <option value="MN" data-flag="🇲🇳" data-code="+976">🇲🇳 +976</option>
                  <option value="ME" data-flag="🇲🇪" data-code="+382">🇲🇪 +382</option>
                  <option value="MS" data-flag="🇲🇸" data-code="+1">🇲🇸 +1</option>
                  <option value="MA" data-flag="🇲🇦" data-code="+212">🇲🇦 +212</option>
                  <option value="MZ" data-flag="🇲🇿" data-code="+258">🇲🇿 +258</option>
                  <option value="MM" data-flag="🇲🇲" data-code="+95">🇲🇲 +95</option>
                  <option value="NA" data-flag="🇳🇦" data-code="+1">🇳🇦 +1</option>
                  <option value="NR" data-flag="🇳🇷" data-code="+674">🇳🇷 +674</option>
                  <option value="NP" data-flag="🇳🇵" data-code="+977">🇳🇵 +977</option>
                  <option value="NL" data-flag="🇳🇱" data-code="+31">🇳🇱 +31</option>
                  <option value="NC" data-flag="🇳🇨" data-code="+687">🇳🇨 +687</option>
                  <option value="NZ" data-flag="🇳🇿" data-code="+64">🇳🇿 +64</option>
                  <option value="NI" data-flag="🇳🇮" data-code="+505">🇳🇮 +505</option>
                  <option value="NE" data-flag="🇳🇪" data-code="+227">🇳🇪 +227</option>
                  <option value="NG" data-flag="🇳🇬" data-code="+234">🇳🇬 +234</option>
                  <option value="NU" data-flag="🇳🇺" data-code="+683">🇳🇺 +683</option>
                  <option value="NF" data-flag="🇳🇫" data-code="+672">🇳🇫 +672</option>
                  <option value="MP" data-flag="🇲🇵" data-code="+1">🇲🇵 +1</option>
                  <option value="NO" data-flag="🇳🇴" data-code="+47">🇳🇴 +47</option>
                  <option value="OM" data-flag="🇴🇲" data-code="+968">🇴🇲 +968</option>
                  <option value="PK" data-flag="🇵🇰" data-code="+92">🇵🇰 +92</option>
                  <option value="PW" data-flag="🇵🇼" data-code="+680">🇵🇼 +680</option>
                  <option value="PS" data-flag="🇵🇸" data-code="+970">🇵🇸 +970</option>
                  <option value="PA" data-flag="🇵🇦" data-code="+507">🇵🇦 +507</option>
                  <option value="PG" data-flag="🇵🇬" data-code="+675">🇵🇬 +675</option>
                  <option value="PY" data-flag="🇵🇾" data-code="+595">🇵🇾 +595</option>
                  <option value="PE" data-flag="🇵🇪" data-code="+51">🇵🇪 +51</option>
                  <option value="PH" data-flag="🇵🇭" data-code="+63">🇵🇭 +63</option>
                  <option value="PN" data-flag="🇵🇳" data-code="+64">🇵🇳 +64</option>
                  <option value="PL" data-flag="🇵🇱" data-code="+48">🇵🇱 +48</option>
                  <option value="PT" data-flag="🇵🇹" data-code="+351">🇵🇹 +351</option>
                  <option value="PR" data-flag="🇵🇷" data-code="+1">🇵🇷 +1</option>
                  <option value="QA" data-flag="🇶🇦" data-code="+966">🇶🇦 +966</option>
                  <option value="RE" data-flag="🇷🇪" data-code="+262">🇷🇪 +262</option>
                  <option value="RO" data-flag="🇷🇴" data-code="+40">🇷🇴 +40</option>
                  <option value="RU" data-flag="🇷🇺" data-code="+7">🇷🇺 +7</option>
                  <option value="RW" data-flag="🇷🇼" data-code="+250">🇷🇼 +250</option>
                  <option value="BL" data-flag="🇧🇱" data-code="+590">🇧🇱 +590</option>
                  <option value="SH" data-flag="🇸🇭" data-code="+290">🇸🇭 +290</option>
                  <option value="KN" data-flag="🇰🇳" data-code="+58">🇰🇳 +58</option>
                  <option value="LC" data-flag="🇱🇨" data-code="+1">🇱🇨 +1</option>
                  <option value="MF" data-flag="🇲🇫" data-code="+590">🇲🇫 +590</option>
                  <option value="PM" data-flag="🇵🇲" data-code="+508">🇵🇲 +508</option>
                  <option value="VC" data-flag="🇻🇨" data-code="+1">🇻🇨 +1</option>
                  <option value="WS" data-flag="🇼🇸" data-code="+685">🇼🇸 +685</option>
                  <option value="SM" data-flag="🇸🇲" data-code="+378">🇸🇲 +378</option>
                  <option value="ST" data-flag="🇸🇹" data-code="+239">🇸🇹 +239</option>
                  <option value="SA" data-flag="🇸🇦" data-code="+966">🇸🇦 +966</option>
                  <option value="SN" data-flag="🇸🇳" data-code="+221">🇸🇳 +221</option>
                  <option value="RS" data-flag="🇷🇸" data-code="+381">🇷🇸 +381</option>
                  <option value="SC" data-flag="🇸🇨" data-code="+248">🇸🇨 +248</option>
                  <option value="SL" data-flag="🇸🇱" data-code="+91">🇸🇱 +91</option>
                  <option value="SG" data-flag="🇸🇬" data-code="+65">🇸🇬 +65</option>
                  <option value="SK" data-flag="🇸🇰" data-code="+421">🇸🇰 +421</option>
                  <option value="SI" data-flag="🇸🇮" data-code="+386">🇸🇮 +386</option>
                  <option value="SB" data-flag="🇸🇧" data-code="+677">🇸🇧 +677</option>
                  <option value="SO" data-flag="🇸🇴" data-code="+252">🇸🇴 +252</option>
                  <option value="ZA" data-flag="🇿🇦" data-code="+49">🇿🇦 +49</option>
                  <option value="GS" data-flag="🇬🇸" data-code="+500">🇬🇸 +500</option>
                  <option value="SS" data-flag="🇸🇸" data-code="+211">🇸🇸 +211</option>
                  <option value="ES" data-flag="🇪🇸" data-code="+34">🇪🇸 +34</option>
                  <option value="LK" data-flag="🇱🇰" data-code="+49">🇱🇰 +49</option>
                  <option value="SD" data-flag="🇸🇩" data-code="+249">🇸🇩 +249</option>
                  <option value="SR" data-flag="🇸🇷" data-code="+597">🇸🇷 +597</option>
                  <option value="SJ" data-flag="🇸🇯" data-code="+47">🇸🇯 +47</option>
                  <option value="SZ" data-flag="🇸🇿" data-code="+268">🇸🇿 +268</option>
                  <option value="SE" data-flag="🇸🇪" data-code="+46">🇸🇪 +46</option>
                  <option value="CH" data-flag="🇨🇭" data-code="+41">🇨🇭 +41</option>
                  <option value="SY" data-flag="🇸🇾" data-code="+963">🇸🇾 +963</option>
                  <option value="TW" data-flag="🇹🇼" data-code="+886">🇹🇼 +886</option>
                  <option value="TJ" data-flag="🇹🇯" data-code="+992">🇹🇯 +992</option>
                  <option value="TZ" data-flag="🇹🇿" data-code="+255">🇹🇿 +255</option>
                  <option value="TH" data-flag="🇹🇭" data-code="+66">🇹🇭 +66</option>
                  <option value="TL" data-flag="🇹🇱" data-code="+670">🇹🇱 +670</option>
                  <option value="TG" data-flag="🇹🇬" data-code="+228">🇹🇬 +228</option>
                  <option value="TK" data-flag="🇹🇰" data-code="+690">🇹🇰 +690</option>
                  <option value="TO" data-flag="🇹🇴" data-code="+676">🇹🇴 +676</option>
                  <option value="TT" data-flag="🇹🇹" data-code="+1">🇹🇹 +1</option>
                  <option value="TN" data-flag="🇹🇳" data-code="+216">🇹🇳 +216</option>
                  <option value="TR" data-flag="🇹🇷" data-code="+90">🇹🇷 +90</option>
                  <option value="TM" data-flag="🇹🇲" data-code="+993">🇹🇲 +993</option>
                  <option value="TC" data-flag="🇹🇨" data-code="+1">🇹🇨 +1</option>
                  <option value="TV" data-flag="🇹🇻" data-code="+688">🇹🇻 +688</option>
                  <option value="UG" data-flag="🇺🇬" data-code="+256">🇺🇬 +256</option>
                  <option value="UA" data-flag="🇺🇦" data-code="+380">🇺🇦 +380</option>
                  <option value="AE" data-flag="🇦🇪" data-code="+49">🇦🇪 +49</option>
                  <option value="GB" data-flag="🇬🇧" data-code="+44">🇬🇧 +44</option>
                  <option value="US" data-flag="🇺🇸" data-code="+1">🇺🇸 +1</option>
                  <option value="UM" data-flag="🇺🇲" data-code="+1">🇺🇲 +1</option>
                  <option value="UY" data-flag="🇺🇾" data-code="+598">🇺🇾 +598</option>
                  <option value="UZ" data-flag="🇺🇿" data-code="+998">🇺🇿 +998</option>
                  <option value="VU" data-flag="🇻🇺" data-code="+678">🇻🇺 +678</option>
                  <option value="VE" data-flag="🇻🇪" data-code="+58">🇻🇪 +58</option>
                  <option value="VN" data-flag="🇻🇳" data-code="+84">🇻🇳 +84</option>
                  <option value="VG" data-flag="🇻🇬" data-code="+1">🇻🇬 +1</option>
                  <option value="VI" data-flag="🇻🇮" data-code="+1">🇻🇮 +1</option>
                  <option value="WF" data-flag="🇼🇫" data-code="+681">🇼🇫 +681</option>
                  <option value="EH" data-flag="🇪🇭" data-code="+212">🇪🇭 +212</option>
                  <option value="YE" data-flag="🇾🇪" data-code="+967">🇾🇪 +967</option>
                  <option value="ZM" data-flag="🇿🇲" data-code="+260">🇿🇲 +260</option>
                  <option value="ZW" data-flag="🇿🇼" data-code="+263">🇿🇼 +263</option>
                </select>
              </div>

              <div className="relative" style={{ width: '458px' }}>
                <input
                  type="tel"
                  id="mobile"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleInputChange}
                  onFocus={() => handleFocus('mobile')}
                  onBlur={handleBlur}
                  className={`w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.mobile ? 'border-red-500' : 'border-gray-300'
                  }`}
                  style={{ fontFamily: 'Swiss-721, Arial, sans-serif' }}
                  pattern="[0-9]*"
                  inputMode="numeric"
                />
                <label 
                  htmlFor="mobile" 
                  className={`absolute left-4 pointer-events-none transition-all duration-300 ${
                    formData.mobile || focusedField === 'mobile' 
                      ? '-top-3 text-xs' 
                      : 'top-1/2 transform -translate-y-1/2 text-sm'
                  }`}
                  style={{ 
                    fontFamily: 'Swiss-721, Arial, sans-serif', 
                    fontWeight: 500, 
                    color: 'rgb(112, 112, 112)',
                    backgroundColor: 'white',
                    padding: '0 4px'
                  }}
                >
                  Mobile Number
                </label>
                {errors.mobile && (
                  <div 
                    className="mt-1 text-red-500 text-sm"
                    style={{ 
                      fontFamily: 'Swiss-721, Arial, sans-serif', 
                      fontSize: '12px',
                      fontWeight: 400
                    }}
                  >
                    {errors.mobile}
                  </div>
                )}
              </div>

              <div className="relative" style={{ width: '458px' }}>
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  onFocus={() => handleFocus('password')}
                  onBlur={handleBlur}
                  className={`w-full px-4 py-3 pr-12 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.password ? 'border-red-500' : 'border-gray-300'
                  }`}
                  style={{ fontFamily: 'Swiss-721, Arial, sans-serif' }}
                />
                <label 
                  htmlFor="password" 
                  className={`absolute left-4 pointer-events-none transition-all duration-300 ${
                    formData.password || focusedField === 'password' 
                      ? '-top-3 text-xs' 
                      : 'top-1/2 transform -translate-y-1/2 text-sm'
                  }`}
                  style={{ 
                    fontFamily: 'Swiss-721, Arial, sans-serif', 
                    fontWeight: 500, 
                    color: 'rgb(112, 112, 112)',
                    backgroundColor: 'white',
                    padding: '0 4px'
                  }}
                >
                  Password
                </label>
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400" />
                  )}
                </button>
                {errors.password && (
                  <div 
                    className="mt-1 text-red-500 text-sm"
                    style={{ 
                      fontFamily: 'Swiss-721, Arial, sans-serif', 
                      fontSize: '12px',
                      fontWeight: 400
                    }}
                  >
                    {errors.password}
                  </div>
                )}
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="rememberMe"
                  name="rememberMe"
                  checked={formData.rememberMe}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="rememberMe" className="ml-2 block" style={{ fontFamily: 'Swiss-721, Arial, sans-serif', fontSize: '14px', fontWeight: 700, color: 'rgb(28, 28, 28)' }}>
                  Remember me
                </label>
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full transition-all duration-200"
                style={{ 
                  fontFamily: 'Swiss-721, Arial, sans-serif',
                  background: '#1c1c1c',
                  color: '#ffffff',
                  fontWeight: 700,
                  textRendering: 'optimizeLegibility',
                  WebkitFontSmoothing: 'antialiased',
                  display: 'flex',
                  outline: 'none',
                  textDecoration: 'none',
                  fontSize: '1rem',
                  height: '40px',
                  padding: '0 1rem',
                  lineHeight: '1.375rem',
                  borderRadius: '52px',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  width: 'auto',
                  minWidth: '8.25rem',
                  border: 'none',
                  overflow: 'visible',
                  textTransform: 'none',
                  WebkitAppearance: 'button',
                  justifyContent: 'center',
                  alignItems: 'center',
                  transition: 'color 0.15s ease-in-out, background-color 0.25s ease-in-out, border-color 0.15s ease-in-out',
                  opacity: isSubmitting ? 0.6 : 1
                }}
                title={!formData.mobile || !formData.password ? "Please fill in all required fields" : ""}
              >
                {isSubmitting ? "Signing In..." : "Sign In"}
              </Button>
            </form>

            <div className="space-y-2">
              <a href="#" className="block" style={{ fontFamily: 'Swiss-721, Arial, sans-serif', fontSize: '14px', fontWeight: 500, color: 'black', textDecoration: 'underline' }}>
                Forgot password
              </a>
              <a href="#" className="block" style={{ fontFamily: 'Swiss-721, Arial, sans-serif', fontSize: '14px', fontWeight: 500, color: 'black', textDecoration: 'underline' }}>
                Activate online account
              </a>
            </div>
          </div>

          {/* Vertical Divider */}
          <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-px bg-gray-300 transform -translate-x-1/2"></div>
          
          {/* Right Section - Join Marriott Bonvoy */}
          <div className="space-y-8 mt-10">
            <div>
              <h1 className="text-gray-900 mb-4" style={{ fontFamily: 'Swiss-721, Arial, sans-serif', fontSize: '22px', fontWeight: 700 }}>
                Join Marriott Bonvoy
              </h1>
              <p className="text-gray-700" style={{ fontFamily: 'Swiss-721, Arial, sans-serif', fontSize: '16px', fontWeight: 500 }}>
                Members receive complimentary Wi-Fi plus other exclusive benefits
              </p>
            </div>

            {/* Benefits Grid */}
            <div className="flex justify-between items-center">
              <div className="flex flex-col items-center">
                <HotelOutlinedIcon style={{ width: '78px', height: '39.75px', color: 'rgb(255, 150, 98)', marginBottom: '12px' }} />
                <p style={{ fontFamily: 'Swiss-721, Arial, sans-serif', fontSize: '12px', fontWeight: 700, color: 'rgb(112, 112, 112)' }}>EARN FREE NIGHTS</p>
              </div>
              
              <div className="flex flex-col items-center">
                <WifiOutlinedIcon style={{ width: '78px', height: '39.75px', color: 'rgb(255, 150, 98)', marginBottom: '12px' }} />
                <p style={{ fontFamily: 'Swiss-721, Arial, sans-serif', fontSize: '12px', fontWeight: 700, color: 'rgb(112, 112, 112)' }}>FREE WI-FI</p>
              </div>
              
              <div className="flex flex-col items-center">
                <SellOutlinedIcon style={{ width: '78px', height: '39.75px', color: 'rgb(255, 150, 98)', marginBottom: '12px', transform: 'scaleX(-1)' }} />
                <p style={{ fontFamily: 'Swiss-721, Arial, sans-serif', fontSize: '12px', fontWeight: 700, color: 'rgb(112, 112, 112)' }}>MEMBER RATES</p>
              </div>
              
              <div className="flex flex-col items-center">
                <CardGiftcardOutlinedIcon style={{ width: '78px', height: '39.75px', color: 'rgb(255, 150, 98)', marginBottom: '12px' }} />
                <p style={{ fontFamily: 'Swiss-721, Arial, sans-serif', fontSize: '12px', fontWeight: 700, color: 'rgb(112, 112, 112)' }}>EXCLUSIVE OFFERS</p>
              </div>
            </div>

            <Button
              asChild
              className="transition-all duration-200"
              style={{ 
                fontFamily: 'Swiss-721, Arial, sans-serif',
                background: '#ffffff',
                color: '#1c1c1c',
                fontWeight: 700,
                textRendering: 'optimizeLegibility',
                WebkitFontSmoothing: 'antialiased',
                display: 'flex',
                outline: 'none',
                textDecoration: 'none',
                fontSize: '1rem',
                height: '40px',
                padding: '0 1rem',
                lineHeight: '1.375rem',
                borderRadius: '52px',
                cursor: 'pointer',
                width: '126.742px',
                minWidth: '126.742px',
                border: '1px solid #1c1c1c',
                overflow: 'visible',
                textTransform: 'none',
                WebkitAppearance: 'button',
                justifyContent: 'center',
                alignItems: 'center',
                transition: 'color 0.15s ease-in-out, background-color 0.25s ease-in-out, border-color 0.15s ease-in-out'
              }}
            >
              <Link to="/signup" style={{ color: 'inherit', textDecoration: 'none' }}>Join for Free</Link>
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile App Promotion Banner */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div 
          className="relative"
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            height: '100px',
            overflow: 'hidden',
            border: '1px solid #eee',
            borderRadius: '0.875rem',
            backgroundColor: '#fff',
            alignItems: 'center',
            textAlign: 'center',
            margin: 0,
            width: 'auto',
            maxWidth: 'none',
            boxShadow: 'rgba(0, 0, 0, 0.08) 0px 0.25rem 1.25rem 0px',
            marginTop: 0,
            fontFamily: 'Swiss-721, Arial, sans-serif',
            fontSize: '1rem',
            fontWeight: 400,
            lineHeight: '1.5rem',
            color: '#1c1c1c'
          }}
        >
          {/* Left: Phone */}
          <div className="flex items-center justify-center p-4">
            <img 
              src="/images/app_download_banner.png" 
              alt="Marriott Bonvoy App"
              style={{
                width: '221px',
                height: '140.12px',
                objectFit: 'contain'
              }}
            />
          </div>
          
          {/* Center: Text - Absolutely Positioned */}
          <div 
            className="absolute inset-0 flex items-center justify-center"
            style={{
              pointerEvents: 'none'
            }}
          >
            <h3 
              className="text-gray-900"
              style={{
                fontFamily: 'Aldine 721 BT',
                fontSize: '29px',
                fontWeight: 400,
                lineHeight: '2.5rem',
                letterSpacing: 'normal',
                fontStyle: 'normal',
                textTransform: 'none',
                textAlign: 'center',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'auto'
              }}
            >
              Download Once. Exclusive Rates, Always.
            </h3>
          </div>
          
          {/* Right: Button and QR Code */}
          <div className="absolute right-4 top-1/2 transform -translate-y-1/2 p-4">
            <div className="text-center flex flex-col items-center justify-center">
              <img 
                src="/images/pdt-App-Download-Sm-Banner.png" 
                alt="QR Code"
                className="w-16 h-16 mb-2"
              />
              <p className="text-xs text-gray-600">Scan Code</p>
            </div>
          </div>
          
          {/* Download Now Button - Absolutely Positioned for Vertical Centering */}
          <div 
            className="absolute right-32 top-1/2 transform -translate-y-1/2"
            style={{
              pointerEvents: 'auto'
            }}
          >
            <Button
              className="transition-all duration-200"
              style={{ 
                fontFamily: 'Swiss-721, Arial, sans-serif',
                background: '#1c1c1c',
                color: '#ffffff',
                fontWeight: 700,
                textRendering: 'optimizeLegibility',
                WebkitFontSmoothing: 'antialiased',
                display: 'flex',
                outline: 'none',
                textDecoration: 'none',
                fontSize: '1rem',
                height: '40px',
                padding: '0 1rem',
                lineHeight: '1.375rem',
                borderRadius: '52px',
                cursor: 'pointer',
                width: 'auto',
                minWidth: '8.25rem',
                border: 'none',
                overflow: 'visible',
                textTransform: 'none',
                WebkitAppearance: 'button',
                justifyContent: 'center',
                alignItems: 'center',
                transition: 'color 0.15s ease-in-out, background-color 0.25s ease-in-out, border-color 0.15s ease-in-out'
              }}
            >
              Download Now
            </Button>
          </div>
        </div>
      </div>

      {/* Accordion Footer */}
      <footer className="bg-[#1c1c1c] text-white py-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="space-y-6">
            {/* Top Destinations */}
            <div className="border-b border-white pb-4">
              <div 
                className="flex items-center justify-between cursor-pointer hover:opacity-80 transition-opacity"
                onClick={() => toggleSection('topDestinations')}
              >
                <h3 className="text-white" style={{ fontFamily: 'Swiss-721, Arial, sans-serif', fontSize: '18px', fontWeight: 700 }}>Top Destinations</h3>
                <span className={`text-white text-sm transition-transform duration-200 ${expandedSections.topDestinations ? 'rotate-180' : 'rotate-0'}`}>▼</span>
              </div>
              
              {/* Hotel Destinations Grid */}
              {expandedSections.topDestinations && (
                <div className="mt-4 grid grid-cols-8 gap-2 text-sm">
                {/* Row 1 */}
                <a href="#" className="text-white hover:text-gray-300 transition-colors">Las Vegas Hotels {'>'}</a>
                <a href="#" className="text-white hover:text-gray-300 transition-colors">Miami Hotels {'>'}</a>
                <a href="#" className="text-white hover:text-gray-300 transition-colors">Chicago Hotels {'>'}</a>
                <a href="#" className="text-white hover:text-gray-300 transition-colors">Tokyo Hotels {'>'}</a>
                <a href="#" className="text-white hover:text-gray-300 transition-colors">Orlando Hotels {'>'}</a>
                <a href="#" className="text-white hover:text-gray-300 transition-colors">Key West Hotels {'>'}</a>
                <a href="#" className="text-white hover:text-gray-300 transition-colors">London Hotels {'>'}</a>
                <a href="#" className="text-white hover:text-gray-300 transition-colors">New York Hotels {'>'}</a>
                
                {/* Row 2 */}
                <a href="#" className="text-white hover:text-gray-300 transition-colors">San Diego Hotels {'>'}</a>
                <a href="#" className="text-white hover:text-gray-300 transition-colors">Boston Hotels {'>'}</a>
                <a href="#" className="text-white hover:text-gray-300 transition-colors">Myrtle Beach Hotels {'>'}</a>
                <a href="#" className="text-white hover:text-gray-300 transition-colors">Austin Hotels {'>'}</a>
                <a href="#" className="text-white hover:text-gray-300 transition-colors">Nashville Hotels {'>'}</a>
                <a href="#" className="text-white hover:text-gray-300 transition-colors">Denver Hotels {'>'}</a>
                <a href="#" className="text-white hover:text-gray-300 transition-colors">Maui Hotels {'>'}</a>
                <div></div>
                
                {/* Row 3 */}
                <a href="#" className="text-white hover:text-gray-300 transition-colors">San Francisco Hotels {'>'}</a>
                <a href="#" className="text-white hover:text-gray-300 transition-colors">New Orleans Hotels {'>'}</a>
                <a href="#" className="text-white hover:text-gray-300 transition-colors">San Juan Hotels {'>'}</a>
                <a href="#" className="text-white hover:text-gray-300 transition-colors">Seattle Hotels {'>'}</a>
                <a href="#" className="text-white hover:text-gray-300 transition-colors">Santa Barbara Hotels {'>'}</a>
                <a href="#" className="text-white hover:text-gray-300 transition-colors">Palm Springs Hotels {'>'}</a>
                <a href="#" className="text-white hover:text-gray-300 transition-colors">Paris Hotels {'>'}</a>
                <div></div>
                
                {/* Row 4 */}
                <a href="#" className="text-white hover:text-gray-300 transition-colors">Rome Hotels {'>'}</a>
                <a href="#" className="text-white hover:text-gray-300 transition-colors">Amsterdam Hotels {'>'}</a>
                <a href="#" className="text-white hover:text-gray-300 transition-colors">Virginia Beach Hotels {'>'}</a>
                <a href="#" className="text-white hover:text-gray-300 transition-colors">Ocean City Hotels {'>'}</a>
                <a href="#" className="text-white hover:text-gray-300 transition-colors">Pigeon Forge Hotels {'>'}</a>
                <a href="#" className="text-white hover:text-gray-300 transition-colors">Lake Tahoe Hotels {'>'}</a>
                <a href="#" className="text-white hover:text-gray-300 transition-colors">Los Angeles Hotels {'>'}</a>
                <div></div>
                
                {/* Row 5 */}
                <a href="#" className="text-white hover:text-gray-300 transition-colors">Gatlinburg Hotels {'>'}</a>
                <a href="#" className="text-white hover:text-gray-300 transition-colors">Kauai Hotels {'>'}</a>
                <a href="#" className="text-white hover:text-gray-300 transition-colors">Savannah Hotels {'>'}</a>
                <a href="#" className="text-white hover:text-gray-300 transition-colors">Galveston Hotels {'>'}</a>
                <a href="#" className="text-white hover:text-gray-300 transition-colors">Charleston Hotels {'>'}</a>
                <a href="#" className="text-white hover:text-gray-300 transition-colors">Moab Hotels {'>'}</a>
                <a href="#" className="text-white hover:text-gray-300 transition-colors">Lisbon Hotels {'>'}</a>
                <div></div>
                
                {/* Row 6 */}
                <a href="#" className="text-white hover:text-gray-300 transition-colors">Traverse City Hotels {'>'}</a>
                <a href="#" className="text-white hover:text-gray-300 transition-colors">Waikiki Hotels {'>'}</a>
                <a href="#" className="text-white hover:text-gray-300 transition-colors">Mexico City Hotels {'>'}</a>
                <div></div>
                <div></div>
                <div></div>
                <div></div>
                <div></div>
              </div>
              )}
            </div>
            
            {/* For Guests */}
            <div className="border-b border-white pb-4">
              <div 
                className="flex items-center justify-between cursor-pointer hover:opacity-80 transition-opacity"
                onClick={() => toggleSection('forGuests')}
              >
                <h3 className="text-white" style={{ fontFamily: 'Swiss-721, Arial, sans-serif', fontSize: '18px', fontWeight: 700 }}>For Guests</h3>
                <span className={`text-white text-sm transition-transform duration-200 ${expandedSections.forGuests ? 'rotate-180' : 'rotate-0'}`}>▼</span>
              </div>
              
              {/* For Guests Links */}
              {expandedSections.forGuests && (
                <div className="mt-4 space-y-2">
                  {/* First Row */}
                  <div className="grid grid-cols-7 gap-4 text-sm">
                    <a href="#" className="text-white hover:text-gray-300 transition-colors">Best Rate Guarantee {'>'}</a>
                    <a href="#" className="text-white hover:text-gray-300 transition-colors">Marriott Bonvoy Moments {'>'}</a>
                    <a href="#" className="text-white hover:text-gray-300 transition-colors">Activities {'>'}</a>
                    <a href="#" className="text-white hover:text-gray-300 transition-colors">Marriott Insiders {'>'}</a>
                    <a href="#" className="text-white hover:text-gray-300 transition-colors">Marriott Bonvoy Traveler {'>'}</a>
                    <a href="#" className="text-white hover:text-gray-300 transition-colors">Travel Experiences {'>'}</a>
                    <a href="#" className="text-white hover:text-gray-300 transition-colors">Travel Insurance {'>'}</a>
                  </div>
                  
                  {/* Second Row */}
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <a href="#" className="text-white hover:text-gray-300 transition-colors">Shop Marriott {'>'}</a>
                    <a href="#" className="text-white hover:text-gray-300 transition-colors">Shop Westin {'>'}</a>
                    <a href="#" className="text-white hover:text-gray-300 transition-colors">Gift Cards {'>'}</a>
                  </div>
                </div>
              )}
            </div>
            
            {/* Our Company */}
            <div className="border-b border-white pb-4">
              <div 
                className="flex items-center justify-between cursor-pointer hover:opacity-80 transition-opacity"
                onClick={() => toggleSection('ourCompany')}
              >
                <h3 className="text-white" style={{ fontFamily: 'Swiss-721, Arial, sans-serif', fontSize: '18px', fontWeight: 700 }}>Our Company</h3>
                <span className={`text-white text-sm transition-transform duration-200 ${expandedSections.ourCompany ? 'rotate-180' : 'rotate-0'}`}>▼</span>
              </div>
              
              {/* Our Company Links */}
              {expandedSections.ourCompany && (
                <div className="mt-4 space-y-2">
                  {/* First Row */}
                  <div className="grid grid-cols-8 gap-4 text-sm">
                    <a href="#" className="text-white hover:text-gray-300 transition-colors">About Marriott {'>'}</a>
                    <a href="#" className="text-white hover:text-gray-300 transition-colors">Careers {'>'}</a>
                    <a href="#" className="text-white hover:text-gray-300 transition-colors">Small & Midsize Business Program {'>'}</a>
                    <a href="#" className="text-white hover:text-gray-300 transition-colors">Travel Professionals {'>'}</a>
                    <a href="#" className="text-white hover:text-gray-300 transition-colors">Group Partners {'>'}</a>
                    <a href="#" className="text-white hover:text-gray-300 transition-colors">Developers {'>'}</a>
                    <a href="#" className="text-white hover:text-gray-300 transition-colors">Affiliates {'>'}</a>
                    <a href="#" className="text-white hover:text-gray-300 transition-colors">Investors {'>'}</a>
                  </div>
                  
                  {/* Second Row */}
                  <div className="grid grid-cols-4 gap-4 text-sm">
                    <a href="#" className="text-white hover:text-gray-300 transition-colors">News {'>'}</a>
                    <a href="#" className="text-white hover:text-gray-300 transition-colors">David Marriott's Blog {'>'}</a>
                    <a href="#" className="text-white hover:text-gray-300 transition-colors">Tony Capuano's Blog {'>'}</a>
                    <a href="#" className="text-white hover:text-gray-300 transition-colors">Heart of the House {'>'}</a>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </footer>

      {/* Extended Footer */}
      <footer className="bg-[#1c1c1c] text-white py-6">
        <div className="max-w-7xl mx-auto px-4">
          {/* Follow Us Section */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-4">
              <span className="text-white font-bold">Follow us</span>
              <div className="flex items-center space-x-8">
                <FacebookOutlinedIcon style={{ color: 'white', fontSize: '20px' }} />
                <i className="fa-brands fa-instagram" style={{ color: 'white', fontSize: '20px' }}></i>
                <span className="text-white text-xl">𝕏</span> {/* Twitter/X */}
                <i className="fa-brands fa-linkedin" style={{ color: 'white', fontSize: '20px' }}></i>
                <i className="fa-brands fa-youtube" style={{ color: 'white', fontSize: '20px' }}></i>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="border-b border-white mb-6"></div>

          {/* Copyright and Language */}
          <div className="flex items-center justify-between mb-6">
            <div className="text-white" style={{ fontFamily: 'Swiss-721, Arial, sans-serif', fontSize: '12px', fontWeight: 700 }}>
              © 1996 – 2025 Marriott International, Inc. All rights reserved. Marriott Proprietary Information
            </div>
            <div className="flex items-center space-x-2 text-white text-sm">
              <span>🌐</span> {/* Globe icon */}
              <span>English</span>
            </div>
          </div>

          {/* Legal Links */}
          <div className="space-y-3">
            <div className="flex flex-wrap items-center text-white space-x-2" style={{ fontFamily: 'Swiss-721, Arial, sans-serif', fontSize: '12px', fontWeight: 700 }}>
              <a href="#" className="hover:underline">Tracking Preferences</a>
              <span>{'>'}</span>
              <a href="#" className="hover:underline">Careers</a>
              <span>{'>'}</span>
              <a href="#" className="hover:underline">Terms of Use</a>
              <span>{'>'}</span>
              <a href="#" className="hover:underline">Program Terms & Conditions</a>
              <span>{'>'}</span>
              <a href="#" className="hover:underline">Privacy Center</a>
              <span>{'>'}</span>
              <div className="flex items-center space-x-1">
                <span className="w-4 h-2 bg-blue-500 rounded-full flex items-center">
                  <span className="w-1 h-1 bg-white rounded-full ml-0.5"></span>
                </span>
                <a href="#" className="hover:underline">Your Privacy Choices</a>
              </div>
              <span>{'>'}</span>
              <a href="#" className="hover:underline">Digital Accessibility</a>
              <span>{'>'}</span>
              <a href="#" className="hover:underline">Sustainability in the Supply Chain</a>
            </div>
            
            <div className="flex flex-wrap items-center text-white space-x-2" style={{ fontFamily: 'Swiss-721, Arial, sans-serif', fontSize: '12px', fontWeight: 700 }}>
              <a href="#" className="hover:underline">Site Map</a>
              <span>{'>'}</span>
              <a href="#" className="hover:underline">Hotel Site Map</a>
              <span>{'>'}</span>
              <a href="#" className="hover:underline">Help</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SignIn;
